import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import client from "../lib/api";
import { Card, SectionTitle } from "../components/common";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [info, setInfo] = useState({});

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await client.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* keep trying */ }
      tries += 1;
      if (tries >= 8) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="max-w-lg mx-auto" data-testid="payment-success-page">
      <SectionTitle kicker="Diaspora support">Thank you</SectionTitle>
      <Card className="p-8 text-center">
        {state === "checking" && (
          <><Loader2 className="animate-spin mx-auto mb-3 text-green" size={40} /><p className="text-sm text-ink/60">Confirming your payment…</p></>
        )}
        {state === "paid" && (
          <div data-testid="payment-paid">
            <div className="w-14 h-14 rounded-full bg-green text-cream flex items-center justify-center mx-auto mb-3"><CheckCircle2 size={30} /></div>
            <h3 className="font-head font-black text-2xl text-green">Payment received</h3>
            <p className="text-sm text-ink/60 mt-1">Your ${Number(info.amount || 0).toFixed(2)} donation to {info.club_name || "your club"} is confirmed. Makorokoto!</p>
            <Link to="/fantasy" className="inline-block mt-5 px-5 py-2.5 bg-gold text-ink font-bold rounded-sm">Back to Fantasy Hub</Link>
          </div>
        )}
        {state === "timeout" && (
          <><Loader2 className="mx-auto mb-3 text-gold" size={40} /><h3 className="font-head font-black text-xl">Still processing</h3><p className="text-sm text-ink/60 mt-1">Your payment is taking a moment to confirm. It will update shortly — no need to pay again.</p></>
        )}
        {state === "error" && (
          <><div className="w-14 h-14 rounded-full bg-live text-white flex items-center justify-center mx-auto mb-3"><XCircle size={30} /></div><h3 className="font-head font-black text-xl">Couldn't confirm payment</h3><p className="text-sm text-ink/60 mt-1">If you were charged, it will still be recorded. Please contact support if unsure.</p><Link to="/" className="inline-block mt-5 px-5 py-2.5 bg-green text-cream font-bold rounded-sm">Home</Link></>
        )}
      </Card>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-lg mx-auto" data-testid="payment-cancel-page">
      <SectionTitle kicker="Diaspora support">Payment cancelled</SectionTitle>
      <Card className="p-8 text-center">
        <p className="text-sm text-ink/60">No charge was made. You can back your club any time.</p>
        <Link to="/fantasy" className="inline-block mt-5 px-5 py-2.5 bg-gold text-ink font-bold rounded-sm">Back to Fantasy Hub</Link>
      </Card>
    </div>
  );
}
